import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { uniq } from 'lodash'
import { Card, CardHeader, CardTitle, CardContent } from '../../ui/card'
import { Badge } from '../../ui/badge'
import SkillButton from '../../leevo_ui/SkillButton/SkillButton'
import SkillSelector from '../../leevo_ui/SkillSelector/SkillSelector'
import { CheckCircle2, Circle, CircleDot, GraduationCap } from 'lucide-react'

function SkillsDetail({ skills }) {
  const skillCategories = skills && skills.length > 0 ? uniq(skills.map((s) => s.category)) : []
  const hasMultipleCategories =
    skillCategories.length > 1 || (skillCategories.length === 1 && skillCategories[0] != null)
  const [selectedCategory, setSelectedCategory] = useState(skillCategories[0] ?? null)
  const showSkillSelector = skillCategories.length > 1

  if (!skills || skills.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-purple-600" />
            <CardTitle>Class Skills</CardTitle>
          </div>
          <p className="text-sm text-gray-600 mt-2">Skills to complete for this level</p>
        </CardHeader>
        <CardContent>
          <p className="text-center text-gray-500 py-8">No skills assigned yet.</p>
        </CardContent>
      </Card>
    )
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />
      case 'introduced':
        return <CircleDot className="w-5 h-5 text-blue-600" />
      default:
        return <Circle className="w-5 h-5 text-gray-300" />
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Completed</Badge>
      case 'introduced':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">In Progress</Badge>
      default:
        return (
          <Badge variant="secondary" className="bg-gray-100 text-gray-600">
            Not Started
          </Badge>
        )
    }
  }

  const categorySkills = hasMultipleCategories
    ? skills.filter((skill) => skill.category === selectedCategory).sort((a, b) => a.ordinal - b.ordinal)
    : skills.slice().sort((a, b) => a.ordinal - b.ordinal)

  const completedCount = categorySkills.filter((s) => s.status === 'completed').length

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-purple-600" />
              <CardTitle>Class Skills</CardTitle>
            </div>
            <Badge variant="secondary" className="text-sm">
              {completedCount} / {categorySkills.length} Completed
            </Badge>
          </div>
          <p className="text-sm text-gray-600">Skills to complete for this level</p>

          <div className="flex flex-wrap items-center gap-4 py-3 px-4 bg-gray-50 rounded-lg border border-gray-200">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Legend:</span>
            <div className="flex items-center gap-2">
              <Circle className="w-4 h-4 text-gray-300" />
              <span className="text-sm text-gray-600">Not Introduced</span>
            </div>
            <div className="flex items-center gap-2">
              <CircleDot className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-gray-600">Introduced</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="text-sm text-gray-600">Completed</span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {showSkillSelector && (
          <div className="mb-6 flex justify-center border-b pb-4">
            <SkillSelector
              skillCategories={skillCategories}
              selectedCategory={selectedCategory}
              onCategorySelected={setSelectedCategory}
            />
          </div>
        )}

        <div className="space-y-2">
          {categorySkills.map(({ id, shortLabel, name, status }) => (
            <div
              key={id}
              className="flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <div className="flex-shrink-0">{getStatusIcon(status)}</div>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <SkillButton label={shortLabel} state={status} />
                <span className="text-base text-gray-900 font-medium">{name}</span>
              </div>
              <div className="flex-shrink-0 hidden sm:block">{getStatusBadge(status)}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

SkillsDetail.propTypes = {
  skills: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      shortLabel: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      category: PropTypes.string,
      status: PropTypes.string.isRequired,
      ordinal: PropTypes.number.isRequired
    })
  ).isRequired
}

export default SkillsDetail
